/**
 * Puzzle 3 — The Kitten Corral.
 *
 * Three frantic kittens scatter through section 3. Both cats are needed
 * (design principle #1):
 *   - Minna's Calming Resonance (Purr) calms them one by one; calmed
 *     kittens then follow her, and she leads them to the kitten pen.
 *   - The pen's gate is sprung shut. It opens only while Basil is anchored
 *     (Pneumatic Anchor, key 3) on the gate plate at the pen's west
 *     gateway. Calm kittens that reach a shut gate wait outside it; once
 *     it opens, any calm kitten near the pen trots in by itself.
 * Frantic kittens never enter the pen, and nothing but an anchored Basil
 * on the plate opens the gate (Minna's weight doesn't, nor does Basil
 * just standing on it). `KITTEN_PENNED` fires with the running count
 * until all three are inside.
 *
 * This puzzle owns the kittens, the pen and the gate plate.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import type { LinesMesh } from "@babylonjs/core/Meshes/linesMesh";

import { KittenPen } from "../../../entities/interactives/KittenPen";
import { PressurePlate } from "../../../entities/interactives/PressurePlate";
import { Kitten } from "../../../entities/npcs/Kitten";

import { GameEvents } from "../../../config/events";
import { COLORS, SCRAPYARD } from "../../../config/GameConfig";
import { tagAs } from "../../PlaceholderTag";
import type { CueDef } from "../../../ui/tutorialCues";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

/** The pen, on the ground under p3_4. Kittens are counted by x/z radius. */
export const KITTEN_PEN_POSITION = { x: 90, y: 2.5 } as const;

/**
 * Gate plate: on the ground at the pen's west gateway, in the open gap
 * between p3_3 (east edge 87) and p3_4 (west edge 88.5), so an anchored
 * Basil has open sky above him and is clear of both ledges.
 */
export const PEN_GATE_PLATE = { x: 87.7, y: 0 } as const;

/**
 * Calm kittens within this distance of the pen's edge are "at the gate":
 * they wait there while it is shut and trot in once it opens.
 */
export const PEN_GATE_REACH = 2.5;

/** A kept-out kitten stays this far outside the pen radius. */
const PEN_KEEP_OUT_MARGIN = 0.15;

/**
 * Seconds a cat must stand on the plate with the gate still shut before
 * the HUD says what the gate needs — long enough to walk over it, or step
 * on and press 3, without a toast.
 */
const GATE_NUDGE_DWELL = 0.8;

// ── HUD cue (LevelCueDriver polls getCue) ────────────────────────────

export type KittenCorralStep = "calm" | "lead" | "gate" | "entering" | "solved";

export interface KittenCorralStepFlags {
  solved: boolean;
  /** Unpenned kittens not calm yet (frantic or mid-calming). */
  frantic: number;
  /** Calm kittens that are not at the pen yet. */
  strays: number;
  /** Calm kittens at the pen (within PEN_GATE_REACH of its edge). */
  waiting: number;
  gateOpen: boolean;
}

/**
 * Which step the HUD points at. Minna calms every kitten and leads them to
 * the pen first; then Basil holds the gate. In co-op Basil can anchor the
 * gate any time — kittens trot in as they arrive.
 */
export function kittenCorralStep(f: KittenCorralStepFlags): KittenCorralStep {
  if (f.solved) return "solved";
  if (f.frantic > 0) return "calm";
  if (f.strays > 0) return "lead";
  if (f.waiting > 0 && !f.gateOpen) return "gate";
  return "entering";
}

/** Cue chip per step (the same object while the step is unchanged). */
export const KITTEN_CORRAL_CUES: Record<KittenCorralStep, CueDef | null> = {
  calm: { cat: "minna", parts: [{ action: "purr", hold: true }, { text: "calm the kitten" }] },
  lead: { cat: "minna", parts: [{ action: "move" }, { text: "lead to the pen" }] },
  gate: { cat: "basil", parts: [{ text: "onto the plate," }, { action: "anchor" }, { text: "hold the gate" }] },
  entering: null,
  solved: null,
};

/** Objective line per step (a few words). */
export const KITTEN_CORRAL_OBJECTIVES: Record<KittenCorralStep, string> = {
  calm: "Calm the frantic kittens",
  lead: "Lead the kittens to the pen",
  gate: "Hold the pen gate open",
  entering: "Corral the kittens",
  solved: "Corral the kittens",
};

/** Cue anchors sit this far above the thing to act on. */
const CUE_LIFT = 1.2;

export class KittenCorralPuzzle implements PuzzleModule {
  readonly id = "kittenCorral";
  readonly displayName = "The Kitten Corral";

  private ctx!: PuzzleContext;

  private kittenPen!: KittenPen;
  private gatePlate!: PressurePlate;
  private gateChain: LinesMesh | null = null;
  private kittens: Kitten[] = [];
  private kittenSpawns: Vector3[] = [];

  private pennedCount = 0;
  private solved = false;
  private gateOpen = false;
  private gateOpenings = 0;
  private elapsed = 0;
  private plateDwell = 0;
  private lastGateToastAt = -Infinity;
  private readonly kittenWanderRadius = 2.5;

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    // ── Kitten pen ──
    this.kittenPen = new KittenPen(scene, {
      key: "kittenPen",
      position: new Vector3(KITTEN_PEN_POSITION.x, KITTEN_PEN_POSITION.y, 0),
      label: "Kitten Pen",
      requiredCount: SCRAPYARD.PUZZLE.KITTENS_TO_CALM,
    });

    // ── Gate plate (Basil anchors here to hold the gate open) ──
    this.gatePlate = new PressurePlate(scene, {
      key: "penGatePlate",
      position: new Vector3(PEN_GATE_PLATE.x, PEN_GATE_PLATE.y, 0),
      label: "Pen Gate Plate",
    });
    this.createGateChain();

    // ── Kittens (start frantic) ──
    this.kittenSpawns = [
      new Vector3(75, 3, 0),
      new Vector3(82, 5, 0),
      new Vector3(95, 3, 0),
    ];
    this.kittens = this.kittenSpawns.map(
      (pos, i) => new Kitten(scene, `kitten_${i}`, pos),
    );

    // ── AuraSight interactives: the pen and its gate plate ──
    ctx.registerInteractive({
      mesh: this.kittenPen.rootMesh,
      hidden: false,
      state: "revealed",
    });
    ctx.registerInteractive({
      mesh: this.gatePlate.rootMesh,
      hidden: false,
      state: "revealed",
    });

    // ── KITTEN_PENNED (from the pen itself — payload has a count) fires
    //    the win condition once every kitten is in. ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.KITTEN_PENNED, (data) => {
        // Two sources emit KITTEN_PENNED: the Kitten (npcKey only) and the
        // KittenPen (count + required). We only care about the pen one.
        if (data && typeof data.count === "number" && typeof data.required === "number") {
          this.pennedCount = data.count;
          if (data.count >= data.required && !this.solved) {
            this.solved = true;
            ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, {
              puzzleId: this.id,
            });
          }
        }
      }),
    );
  }

  update(dt: number, elapsed: number): void {
    this.elapsed = elapsed;
    const { minna, basil, calmingResonance, pneumaticAnchor, papooseCarrier } = this.ctx;
    const isPurring = calmingResonance.isActive;

    // ── The gate: open only while Basil is anchored on its plate ──
    const platePressed = this.gatePlate.checkPressure([minna.mesh, basil.mesh]);
    const basilHolds =
      pneumaticAnchor.isActive && this.gatePlate.isOccupiedBy(basil.mesh.position);
    if (basilHolds !== this.gateOpen) this.setGateOpen(basilHolds);
    this.plateDwell = platePressed && !this.gateOpen ? this.plateDwell + dt : 0;
    if (this.plateDwell >= GATE_NUDGE_DWELL) this.nudgeGate();
    this.kittenPen.tickGate(dt);

    const penX = this.kittenPen.rootMesh.position.x;
    const penY = this.kittenPen.rootMesh.position.y;

    for (let i = 0; i < this.kittens.length; i++) {
      const kitten = this.kittens[i];
      if (kitten.isPenned) continue;

      // Skip the purr / follow state machine for kittens already
      // loaded into the papoose — the carrier forces them to stay
      // calmed, and we don't want `updateBehavior` to overwrite their
      // parented-to-Minna mesh position.
      const isCarried = papooseCarrier?.carriedKittens.includes(kitten) ?? false;
      if (!isCarried) {
        kitten.updateBehavior(
          dt,
          isPurring,
          minna.mesh.position.x,
          minna.mesh.position.y,
        );
        if (kitten.behavior.state === "frantic") {
          const spawnX = this.kittenSpawns[i]?.x ?? kitten.mesh.position.x;
          kitten.mesh.position.x = Math.max(
            spawnX - this.kittenWanderRadius,
            Math.min(spawnX + this.kittenWanderRadius, kitten.mesh.position.x),
          );
        }
      }
      kitten.update(elapsed, dt);

      if (isCarried) continue;

      const isCalmOrFollowing =
        kitten.behavior.isCalmed || kitten.behavior.state === "following";

      // Only a calm kitten goes through an open gate; everyone else stays
      // outside the pen.
      if (!isCalmOrFollowing || !this.gateOpen) this.keepOutOfPen(kitten, penX);

      // Calmed kittens follow Minna — or, once they are at an open gate,
      // trot into the pen. setFollowTarget transitions calmed→following
      // the FIRST time it fires; on later frames update target.x directly
      // so the kitten tracks its current target instead of a stale one
      // (Opossum.ts:156-157 has the same pattern).
      const atGate = this.isAtGate(kitten.mesh.position.x, penX);
      const target = this.gateOpen && atGate
        ? { x: penX, y: penY }
        : { x: minna.mesh.position.x, y: minna.mesh.position.y };
      if (kitten.behavior.isCalmed && kitten.behavior.state !== "following") {
        kitten.setFollowTarget(target);
      } else if (kitten.behavior.state === "following" && kitten.behavior.followTarget) {
        kitten.behavior.followTarget = target;
      }

      // Hoodie Papoose load — canon p.13. When Minna is wearing the
      // papoose, holding Purr near a calmed kitten attaches the
      // kitten to her back. The carrier enforces its own proximity
      // check; this block only triggers the attempt. Per-frame call
      // is safe — the carrier refuses to re-load an already-carried
      // kitten, so spamming purr doesn't stack duplicates.
      if (
        papooseCarrier?.isWorn &&
        !papooseCarrier.isFull &&
        isPurring &&
        isCalmOrFollowing
      ) {
        papooseCarrier.tryLoadKitten(kitten);
      }

      // Calmed/following kittens are safe once they are through the gate.
      if (isCalmOrFollowing && this.gateOpen) {
        if (this.kittenPen.isInsidePen(kitten.mesh.position)) {
          kitten.pen();
          this.kittenPen.penKitten();
        }
      }
    }

    // Papoose auto-unload when Minna enters the pen radius with the gate
    // open. Each carried kitten is released at the pen position and
    // counted the same way a walking kitten is. This is what sells the
    // "carry across the dangerous trail" beat per canon.
    if (
      this.gateOpen &&
      papooseCarrier?.isWorn &&
      papooseCarrier.carriedKittens.length > 0 &&
      this.kittenPen.isInsidePen(minna.mesh.position)
    ) {
      const released = papooseCarrier.unloadAll(
        this.kittenPen.rootMesh.position,
        "pen",
      );
      for (const kitten of released) {
        if (kitten.isPenned) continue;
        kitten.pen();
        this.kittenPen.penKitten();
      }
    }
  }

  private setGateOpen(open: boolean): void {
    this.gateOpen = open;
    this.kittenPen.setGateOpen(open);
    if (open) this.gateOpenings += 1;
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
      puzzleId: this.id,
      step: open ? "gateOpened" : "gateShut",
    });
  }

  /** Someone is on the plate but the gate stays shut: say what it needs. */
  private nudgeGate(): void {
    if (this.elapsed - this.lastGateToastAt < 5) return;
    this.lastGateToastAt = this.elapsed;
    const basilOn = this.gatePlate.isOccupiedBy(this.ctx.basil.mesh.position);
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message: basilOn
        ? "The gate's spring snaps it shut. Basil has to anchor on the plate (3) to hold it open."
        : "Too light to hold the gate open. It needs Basil anchored on the plate (3).",
    });
  }

  private isAtGate(x: number, penX: number): boolean {
    return Math.abs(x - penX) <= this.kittenPen.penRadius + PEN_GATE_REACH;
  }

  /** Push a kitten that is inside the pen radius back out to the nearer side. */
  private keepOutOfPen(kitten: Kitten, penX: number): void {
    const dx = kitten.mesh.position.x - penX;
    const edge = this.kittenPen.penRadius + PEN_KEEP_OUT_MARGIN;
    if (Math.abs(dx) >= edge) return;
    kitten.mesh.position.x = penX + (dx > 0 ? edge : -edge);
  }

  /** A brass chain from the gate plate up to the gate. */
  private createGateChain(): void {
    const pen = this.kittenPen.rootMesh.position;
    const chain = CreateLines(
      "penGate_chain",
      {
        points: [
          new Vector3(PEN_GATE_PLATE.x, PEN_GATE_PLATE.y + 0.1, -0.2),
          new Vector3(PEN_GATE_PLATE.x, pen.y + 0.9, -0.2),
          new Vector3(pen.x - 2.3, pen.y + 1.1, -0.2),
        ],
      },
      this.ctx.scene,
    );
    chain.color = Color3.FromHexString(COLORS.BRASS);
    chain.isPickable = false;
    tagAs(chain, "modular-final");
    this.gateChain = chain;
  }

  getState(): PuzzleState {
    if (this.solved) return { solved: true, progress: 1 };
    return {
      solved: false,
      progress: this.pennedCount / SCRAPYARD.PUZZLE.KITTENS_TO_CALM,
    };
  }

  /** Whether the pen gate is open (Basil anchored on its plate). */
  isGateOpen(): boolean {
    return this.gateOpen;
  }

  /** Step counts for the HUD cue. */
  private stepFlags(): KittenCorralStepFlags {
    const { minna, papooseCarrier } = this.ctx;
    const penX = this.kittenPen.rootMesh.position.x;
    const minnaAtPen = this.isAtGate(minna.mesh.position.x, penX);
    let frantic = 0;
    let strays = 0;
    let waiting = 0;
    for (const kitten of this.kittens) {
      if (kitten.isPenned) continue;
      if (papooseCarrier?.carriedKittens.includes(kitten)) {
        if (minnaAtPen) waiting++; else strays++;
        continue;
      }
      const calm = kitten.behavior.isCalmed || kitten.behavior.state === "following";
      if (!calm) frantic++;
      else if (this.isAtGate(kitten.mesh.position.x, penX)) waiting++;
      else strays++;
    }
    return { solved: this.solved, frantic, strays, waiting, gateOpen: this.gateOpen };
  }

  /** The step the HUD cue points at (see `kittenCorralStep`). */
  getStep(): KittenCorralStep {
    return kittenCorralStep(this.stepFlags());
  }

  /**
   * HUD guidance for LevelCueDriver: the frantic kitten nearest Minna
   * (Purr), then the pen (lead them there), then the gate plate (Basil
   * anchors).
   */
  getCue(): PuzzleCue | null {
    const step = this.getStep();
    if (step === "solved") return null;
    const cue = KITTEN_CORRAL_CUES[step];
    const objective = KITTEN_CORRAL_OBJECTIVES[step];
    const at = this.cueTarget(step);
    if (!cue || !at) return { objective, cue: null };
    return { objective, cue, anchor: { x: at.x, y: at.y + CUE_LIFT, z: 0 } };
  }

  private cueTarget(step: KittenCorralStep): { x: number; y: number } | null {
    switch (step) {
      case "calm": {
        const m = this.ctx.minna.mesh.position;
        let best: Kitten | null = null;
        let bestD = Infinity;
        for (const kitten of this.kittens) {
          if (kitten.isPenned) continue;
          const calm = kitten.behavior.isCalmed || kitten.behavior.state === "following";
          if (calm) continue;
          const d = Math.abs(kitten.mesh.position.x - m.x);
          if (d < bestD) { bestD = d; best = kitten; }
        }
        return best ? best.mesh.getAbsolutePosition() : null;
      }
      case "lead":
        return this.kittenPen.rootMesh.position;
      case "gate":
        return { x: PEN_GATE_PLATE.x, y: PEN_GATE_PLATE.y };
      default:
        return null;
    }
  }

  getInternals() {
    return {
      id: this.id,
      pennedCount: this.pennedCount,
      solved: this.solved,
      step: this.getStep(),
      gate: {
        open: this.gateOpen,
        openings: this.gateOpenings,
        plate: { ...PEN_GATE_PLATE },
        platePressed: this.gatePlate.pressed,
      },
      pen: {
        x: this.kittenPen.rootMesh.position.x,
        y: this.kittenPen.rootMesh.position.y,
        z: this.kittenPen.rootMesh.position.z,
        radius: this.kittenPen.penRadius,
      },
      kittens: this.kittens.map((kitten) => ({
        key: kitten.npcKey,
        x: kitten.mesh.getAbsolutePosition().x,
        y: kitten.mesh.getAbsolutePosition().y,
        z: kitten.mesh.getAbsolutePosition().z,
        state: kitten.behavior.state,
        isPenned: kitten.isPenned,
      })),
    };
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "kittenPen",     position: { x: KITTEN_PEN_POSITION.x, y: KITTEN_PEN_POSITION.y }, radius: 1.8, allowedCat: "either", label: "Kitten pen (deliver here)" },
        // Basil anchors (3) on this plate to hold the pen gate open. Open
        // ground in the gap between p3_3 and p3_4.
        { id: "gate_plate",    position: { x: PEN_GATE_PLATE.x, y: PEN_GATE_PLATE.y + 0.7 }, radius: 0.6, allowedCat: "basil", label: "Pen gate plate (Basil anchors to open the gate)" },
        // Use SPAWN positions, not live mesh positions: kittens jitter
        // at ~2.4u/s while frantic (NPCBehavior updateFrantic) and a
        // live-mesh hint can drift the cat off the platform chasing
        // a moving target. Purr radius (~6u) is wide enough that
        // landing within 2.5u of spawn covers the kitten wherever it
        // jittered to in the meantime.
        { id: "kitten_spawn_0", position: { x: 75, y: 3 }, radius: 2.5, allowedCat: "minna", label: "Kitten 0 spawn" },
        { id: "kitten_spawn_1", position: { x: 82, y: 5 }, radius: 2.5, allowedCat: "minna", label: "Kitten 1 spawn" },
        { id: "kitten_spawn_2", position: { x: 95, y: 3 }, radius: 2.5, allowedCat: "minna", label: "Kitten 2 spawn" },
        // Staircase landing markers — used as `to` for each running-jump
        // recipe. Tight radius so the success check fires reliably when
        // the cat lands on the target plat.
        { id: "p3_2_top",   position: { x: 80.5, y: 4.4 }, radius: 1.0, allowedCat: "either", label: "p3_2 landing" },
        { id: "p3_3_top",   position: { x: 85.0, y: 3.4 }, radius: 1.25, allowedCat: "either", label: "p3_3 landing" },
        { id: "p3_3_west",  position: { x: 84.0, y: 3.4 }, radius: 1.0, allowedCat: "either", label: "p3_3 west parking" },
        { id: "p3_4_top",   position: { x: 90.0, y: 4.9 }, radius: 1.0, allowedCat: "either", label: "p3_4 landing" },
        { id: "p3_4_east",  position: { x: 89.0, y: 4.9 }, radius: 1.0, allowedCat: "either", label: "p3_4 reverse landing" },
        { id: "p3_4_launch", position: { x: 90.4, y: 4.9 }, radius: 1.0, allowedCat: "either", label: "p3_4 launch point" },
        { id: "p3_4_pen_handoff", position: { x: 90.7, y: 4.9 }, radius: 1.0, allowedCat: "either", label: "p3_4 pen handoff" },
        { id: "p3_5_top",   position: { x: 95.6, y: 3.9 }, radius: 1.0, allowedCat: "either", label: "p3_5 landing" },
      ],
      traversals: [
        // Section-3 staircase hops. Each gap is small (1.25-1.5u) and
        // the heights swing both ways; the navigator's auto-planner
        // oscillates on these because plat-vs-plat dy is between
        // toleranceY and gap-override threshold (see KittenCorral
        // docstring for the historical analysis). Hand-authored
        // running jumps clear each hop deterministically.
        //
        // Recipe shape: keyDown a, wait 600ms (vx ramps to MOVE_SPEED=5
        // and cat reaches launch x), keyDown space, wait 80ms, keyUp
        // space, wait 700ms (airtime), keyUp a. Cat covers ~3u east
        // pre-jump + ~3.5u during airtime = lands on next plat.
        {
          id: "running_jump_p3_1_to_p3_2",
          kind: "jump",
          cat: "either",
          from: "kitten_spawn_0",
          to: "p3_2_top",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 450 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            { waitMs: 800 },
            { keyUp: "d" },
          ],
        },
        // p3_2 (top 3.7) → p3_3 (top 2.7) — easier (drop down).
        {
          id: "running_jump_p3_2_to_p3_3",
          kind: "jump",
          cat: "either",
          from: "p3_2_top",
          to: "p3_3_top",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 300 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            // Keep eastward input through the landing. The shorter
            // 250ms hold reached the p3_3 edge but missed the centered
            // QA success target often enough to make fullplay flaky.
            { waitMs: 600 },
            { keyUp: "d" },
            // The traversal success check is X-based; wait for the fall
            // to finish so the next parking walk starts grounded.
            { waitMs: 700 },
          ],
        },
        {
          id: "minna_short_drop_p3_2_to_p3_3",
          kind: "jump",
          cat: "minna",
          from: "p3_2_top",
          to: "p3_3_top",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 200 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            { waitMs: 100 },
            { keyUp: "d" },
            { waitMs: 250 },
            { keyDown: "d" },
            { waitMs: 300 },
            { keyUp: "d" },
          ],
        },
        // p3_3 (top 2.7) → p3_4 (top 4.2) — Δy=+1.5.
        {
          id: "walk_p3_3_to_west_parking",
          kind: "walk",
          cat: "either",
          from: "p3_3_top",
          to: "p3_3_west",
          successWindowMs: 2_000,
          recipe: [
            { keyDown: "a" },
            { waitMs: 450 },
            { keyUp: "a" },
          ],
        },
        {
          id: "running_jump_p3_3_to_p3_4",
          kind: "jump",
          cat: "either",
          from: "p3_3_top",
          to: "p3_4_top",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "d" },
            // Launch before the p3_3 east edge. A 600ms runway lets
            // Minna step into the gap before jump buffering can fire.
            { waitMs: 450 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            { waitMs: 300 },
            { keyUp: "d" },
            { waitMs: 700 },
          ],
        },
        // p3_4 (top 4.2) → p3_5 (top 3.2) — drop down + 1u east gap.
        {
          id: "walk_p3_4_to_pen_handoff",
          kind: "walk",
          cat: "either",
          from: "p3_4_top",
          to: "p3_4_pen_handoff",
          successWindowMs: 2_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 400 },
            { keyUp: "d" },
          ],
        },
        {
          id: "walk_p3_4_pen_handoff_to_launch",
          kind: "walk",
          cat: "either",
          from: "p3_4_pen_handoff",
          to: "p3_4_launch",
          successWindowMs: 2_000,
          recipe: [
            { keyDown: "a" },
            { waitMs: 250 },
            { keyUp: "a" },
          ],
        },
        {
          id: "running_jump_p3_4_to_p3_5",
          kind: "jump",
          cat: "either",
          from: "p3_4_top",
          to: "p3_5_top",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 400 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            { waitMs: 700 },
            { keyUp: "d" },
          ],
        },
        // p3_5 back to p3_4 — used by the no-seed kitten-corral trace
        // after calming the east kitten close up.
        {
          id: "running_jump_p3_5_to_p3_4",
          kind: "jump",
          cat: "either",
          from: "p3_5_top",
          to: "p3_4_east",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "a" },
            { waitMs: 500 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            { waitMs: 700 },
            { keyUp: "a" },
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     kittensPenned?: number,  // number of kittens (0..required) to mark penned
   *     solved?: boolean,        // pen every kitten + emit PUZZLE_SOLVED
   *   }
   *
   * For traces checking downstream Elder cutscene sequencing,
   * `{ solved: true }` skips the entire purr+escort+gate sub-game. The
   * granular `kittensPenned: 2` variant is useful for testing the
   * last-kitten beat in isolation.
   */
  seed(state: Record<string, unknown>): void {
    const required = SCRAPYARD.PUZZLE.KITTENS_TO_CALM;
    let targetCount = -1;
    if (state.solved === true) {
      targetCount = required;
    } else if (typeof state.kittensPenned === "number") {
      targetCount = Math.max(0, Math.min(required, Math.floor(state.kittensPenned)));
    }
    if (targetCount < 0) return;

    // Pen kittens one at a time, mirroring the per-frame
    // `this.kittenPen.penKitten()` path so the KittenPen emits
    // KITTEN_PENNED with running counts and fires PUZZLE_SOLVED through
    // our own handler when the required count is reached.
    let penned = 0;
    for (const kitten of this.kittens) {
      if (penned >= targetCount) break;
      if (kitten.isPenned) continue;
      kitten.pen();
      this.kittenPen.penKitten();
      penned += 1;
    }
  }

  dispose(): void {
    this.kittenPen.dispose();
    this.gatePlate.dispose();
    this.gateChain?.dispose();
    for (const kitten of this.kittens) kitten.dispose();
  }
}
